import React, { useState, useEffect } from 'react';
import { getStripeConfig, saveStripeConfig } from '../services/stripeConfigService';

const StripeSettings = () => {
  const [config, setConfig] = useState({ mode: 'live', publishableKeyTest: '', publishableKeyLive: '' });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStripeConfig()
      .then(setConfig)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await saveStripeConfig(config);
      setStatus({ type: 'success', message: 'Stripe settings saved. Changes take effect within 60 seconds.' });
    } catch (err) {
      setStatus({ type: 'error', message: `Failed to save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="settings-section"><p>Loading Stripe settings...</p></div>;

  return (
    <div className="settings-section">
      <h3>Stripe Mode</h3>
      <p className="settings-description">
        Switch between live and test mode. In test mode, use test card <code>4242 4242 4242 4242</code>.
        Backend functions switch automatically within 60 s of saving.
      </p>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
        {['live', 'test'].map((m) => (
          <label
            key={m}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              border: `2px solid ${config.mode === m ? '#2962ff' : '#e2e8f0'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              background: config.mode === m ? '#eff6ff' : '#fff',
              fontWeight: config.mode === m ? 600 : 400,
            }}
          >
            <input
              type="radio"
              name="stripe-mode"
              value={m}
              checked={config.mode === m}
              onChange={() => setConfig((c) => ({ ...c, mode: m }))}
              style={{ appearance: 'auto', WebkitAppearance: 'radio', accentColor: '#2962ff' }}
            />
            {m === 'live' ? '🟢 Live (Production)' : '🧪 Test (Sandbox)'}
          </label>
        ))}
      </div>

      {config.mode === 'test' && (
        <div className="form-group" style={{ marginBottom: '16px' }}>
          <label className="form-label">Test Publishable Key (pk_test_...)</label>
          <input
            type="text"
            className="form-control"
            placeholder="pk_test_..."
            value={config.publishableKeyTest}
            onChange={(e) => setConfig((c) => ({ ...c, publishableKeyTest: e.target.value }))}
          />
          <p className="form-hint" style={{ marginTop: '4px', color: '#6b7280', fontSize: '13px' }}>
            Used by the portal payment page. The test secret key must also be set in Firebase Secret Manager as <code>STRIPE_SECRET_KEY_TEST</code>.
          </p>
        </div>
      )}

      {config.mode === 'live' && (
        <div className="form-group" style={{ marginBottom: '16px' }}>
          <label className="form-label">Live Publishable Key (pk_live_... — optional override)</label>
          <input
            type="text"
            className="form-control"
            placeholder="Leave blank to use VITE_STRIPE_PUBLISHABLE_KEY from .env"
            value={config.publishableKeyLive}
            onChange={(e) => setConfig((c) => ({ ...c, publishableKeyLive: e.target.value }))}
          />
        </div>
      )}

      {status && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '12px',
            background: status.type === 'success' ? '#f0fdf4' : '#fef2f2',
            color: status.type === 'success' ? '#166534' : '#991b1b',
            border: `1px solid ${status.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          }}
        >
          {status.message}
        </div>
      )}

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Stripe Settings'}
      </button>

      {config.mode === 'test' && (
        <div style={{ marginTop: '20px', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px' }}>
          <strong>To complete test mode setup:</strong>
          <ol style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <li>Set the test secret key in Firebase: <code>firebase functions:secrets:set STRIPE_SECRET_KEY_TEST --project fusionict-billing</code></li>
            <li>Set the test webhook secret: <code>firebase functions:secrets:set STRIPE_WEBHOOK_SECRET_TEST --project fusionict-billing</code></li>
            <li>Deploy functions: <code>firebase deploy --only functions --project fusionict-billing</code></li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default StripeSettings;
